import classNames from 'classnames';
import type { AriaRole, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import Caret from '../../../../components/Caret';
import Popover from '../../../../components/Popover';
import SVGIcon from '../../../../components/SVGIcon';
import Tooltip from '../../../../components/Tooltip';
import addTestId from '../../../../utils/addTestId';
import Link from '../../../../utils/router/Link';
import useControlledState from '../../../../utils/useControlledState';
import normalizeShortcut from '../../../utils/normalizeShortcut';
import * as styles from './ToolbarButton.module.scss';

// Align the pop-over horizontally with the button, compensating for the border
const GROUP_BORDER_COMPENSATION = 1;

interface ToolbarButtonGroupProps {
  'aria-label'?: string;
  role?: AriaRole;
  children: ReactNode;
}

export const ToolbarButtonGroup = forwardRef<
  HTMLDivElement,
  ToolbarButtonGroupProps
>(({ 'aria-label': ariaLabel, role, children }, ref) => (
  <div ref={ref} role={role} aria-label={ariaLabel} className={styles.group}>
    {children}
  </div>
));

type ToolbarButtonProps = {
  icon?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  renderPopOver?: (onDismiss: () => void) => ReactNode;
  popOverHideCaret?: boolean;
  popOverOpen?: boolean;
  onPopOverOpenChange?: (open: boolean) => void;
  rightAlignPopOver?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  to?: string;
  testId?: string;
  accessibleType?: 'toggle' | 'radio';
  'aria-label'?: string;
  newTab?: React.ComponentProps<typeof Link>['newTab'];
} & ({ tooltip?: never } | { tooltip: string; shortcut?: string });

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    {
      icon,
      accessibleType,
      active = false,
      disabled,
      renderPopOver,
      popOverHideCaret,
      rightAlignPopOver,
      children,
      onClick,
      to,
      testId,
      'aria-label': ariaLabel,
      newTab,
      ...props
    }: ToolbarButtonProps,
    ref,
  ) => {
    const [isPopoverOpen, setIsPopoverOpen] = useControlledState({
      defaultValue: false,
      value: props.popOverOpen,
      onValueChange: props.onPopOverOpenChange,
    });

    const caret =
      renderPopOver && !popOverHideCaret && (isPopoverOpen ? 'up' : 'down');
    const iconNode = icon && (
      <span aria-hidden className={styles.icon}>
        {typeof icon === 'string' ? (
          <SVGIcon inline size="small">
            {icon}
          </SVGIcon>
        ) : (
          icon
        )}
      </span>
    );

    let button = (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={classNames(styles.button, {
          [styles.active]: active,
          [styles.selected]: renderPopOver && isPopoverOpen,
        })}
        {...addTestId(testId)}
        aria-checked={accessibleType === 'radio' ? active : undefined}
        aria-pressed={accessibleType === 'toggle' ? active : undefined}
        role={accessibleType === 'radio' ? 'radio' : undefined}
        aria-label={
          ariaLabel ||
          (typeof props.tooltip === 'string'
            ? props.tooltip.replace(/\n/g, ' ')
            : undefined)
        }
      >
        {iconNode}
        {!!children && <div className={styles.label}>{children}</div>}
        {caret && (
          <div className={styles.caret}>
            <Caret direction={caret} />
          </div>
        )}
      </button>
    );

    if (to) {
      button = (
        <Link className={styles.link} to={to} newTab={newTab}>
          {button}
        </Link>
      );
    }

    if (renderPopOver) {
      button = (
        <Popover
          open={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
          placement={rightAlignPopOver ? 'bottom-end' : 'bottom-start'}
          portalContainer="disabled"
          offset={{
            mainAxis: 5,
            crossAxis: rightAlignPopOver
              ? GROUP_BORDER_COMPENSATION
              : -GROUP_BORDER_COMPENSATION,
          }}
          view={
            <div className={styles.popOver}>
              <div className={styles.container}>
                {renderPopOver(() => {
                  setIsPopoverOpen(false);
                })}
              </div>
            </div>
          }
        >
          {button}
        </Popover>
      );
    }

    if (props.tooltip) {
      button = (
        <Tooltip
          display="block"
          content={
            <>
              {props.tooltip}
              {props.shortcut && (
                <>
                  <br />
                  <Tooltip.Muted className={styles.shortcut}>
                    {normalizeShortcut(props.shortcut)}
                  </Tooltip.Muted>
                </>
              )}
            </>
          }
          offset={2}
        >
          {button}
        </Tooltip>
      );
    }

    return <div className={styles.container}>{button}</div>;
  },
);

export default ToolbarButton;
